import type { MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';
import { Launch } from '@carbon/react/icons';
import { Link, PlainLink } from 'components/link';
import styles from './launch-link.module.scss';

interface LaunchLinkBaseProps {
  /**
   * Accessible name. Required: the icon-only form has no text of its own, and
   * "Open X" reads far better in a screen reader than the bare glyph.
   */
  label: string;
  /** Optional visible text rendered before the icon (e.g. "View Case"). */
  children?: ReactNode;
  /** Extra class for the caller's own layout/colour. */
  className?: string;
  /** Icon px size (default 16). */
  iconSize?: number;
  /**
   * Stop the click reaching an enclosing clickable row/tile. Off by default —
   * turn it on only where a parent would otherwise also handle the click.
   */
  stopClickPropagation?: boolean;
}

/** Exactly one target: an in-app route (`to`) or an external URL (`href`). */
type LaunchLinkTarget =
  | {
      /** In-app route (same shape as any router link `to`). */
      to: string;
      href?: never;
    }
  | {
      /** External URL, rendered as a plain anchor. */
      href: string;
      to?: never;
    };

export type LaunchLinkProps = LaunchLinkBaseProps & LaunchLinkTarget;

const stopPropagation = (event: MouseEvent<HTMLAnchorElement>) =>
  event.stopPropagation();

/**
 * A link that opens its target in a NEW TAB, labelled with Carbon's Launch
 * (open-in-new-tab) glyph.
 *
 * The point of the component is that the glyph and the behaviour cannot drift
 * apart. Hand-rolled versions of this pattern got it wrong in both directions:
 * one showed the Launch icon on a plain same-tab link, and another opened a new
 * tab via a bare `target="_blank"` with no `rel`, leaving the opened page a
 * `window.opener` handle back. Here the icon IS the new-tab affordance and
 * `openInNewTab` (from the shared Link, which bundles `target` with
 * `rel="noopener noreferrer"`) is not optional.
 *
 * Pass `to` for in-app routes and `href` for external URLs (Salesforce,
 * FracFocus, …). The split exists because react-router-dom v5's `Link` treats
 * `to="https://…"` as an in-app path; `href` renders the shared `PlainLink`
 * instead. Everything else behaves identically in both modes.
 *
 * `stopClickPropagation` is for the case where this sits inside something else
 * clickable — a row, a selectable tile — that must not also fire. It is opt-in:
 * stopping propagation unconditionally would be an invisible default that no
 * caller could turn off.
 *
 * @param {LaunchLinkProps} props - Target, accessible label, optional text/class/size.
 * @return {JSX.Element} The new-tab link.
 */
export function LaunchLink(props: LaunchLinkProps) {
  const {
    label,
    children,
    className,
    iconSize = 16,
    stopClickPropagation = false,
  } = props;

  const shared = {
    'openInNewTab': true,
    'className': classNames(styles.launchLink, className),
    'aria-label': label,
    // Tooltip only for the bare icon: on a text link it just repeats the text
    // (and doubles up with any tooltip the caller already wraps it in).
    'title': children ? undefined : label,
    'onClick': stopClickPropagation ? stopPropagation : undefined,
  };
  const content = (
    <>
      {children}
      <Launch size={iconSize} />
    </>
  );

  return props.href !== undefined ? (
    <PlainLink href={props.href} {...shared}>
      {content}
    </PlainLink>
  ) : (
    <Link to={props.to} {...shared}>
      {content}
    </Link>
  );
}
